/**
 * Der Ladepunkt als Bedienfläche (HM-119, ADR 0036, `ui_kits/dashboard/control.jsx`).
 *
 * **Zwei Ansichten, ein Zustand.** Die Seite „Laden" und der Reiter „Steuerung"
 * am Gerät bedienen denselben Ladepunkt; beide lesen aus demselben
 * Abfrageschlüssel (`["charge-points", …]`) und laden nach jedem Auftrag
 * gemeinsam nach. Zwei getrennte Zustände widersprächen sich, sobald einer von
 * beiden etwas schickt.
 *
 * Drei Regeln aus dem Entwurf tragen beide:
 *
 * 1. **Ein Bedienelement stellt nichts, es beauftragt.** Bis zur Quittung
 *    zeigt es den **gemeldeten** Stand und daneben, was unterwegs ist — eine
 *    Fläche, die sofort umspringt, behauptete einen Erfolg, den keiner geprüft
 *    hat (HM-040).
 * 2. **Ohne Verbindung wird nicht bedient.** Die Flächen sind dann gesperrt und
 *    sagen, warum; entschieden hat es die API (`controlBlockedBy`), und sie
 *    lehnt einen Auftrag ohnehin ab.
 * 3. **Was nicht gemeldet ist, steht nicht da.** Eine Einstellung, die die
 *    Wallbox nicht schreibbar meldet, bekommt keine Fläche (Regel 10).
 *
 * **Anders als im Entwurf gehen Grenzen nicht gesammelt hinaus.** Der
 * Posteingang eines Geräts hat ein Fach (HM-040); vier Einstellungen in einem
 * Auftrag gibt es auf der Leitung nicht, und vier nacheinander ließe der zweite
 * scheitern. Jede Grenze hat deshalb ihr eigenes „Setzen".
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, EmptyState, Gauge, SegmentedControl, icons } from "@hausmeter/ui";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { formatEnergyFull, formatPowerFull, POWER_UNIT, powerParts } from "../lib/energy-format.js";
import { formatTimeAgo } from "../lib/format.js";
import { COMMAND_STATUS_LABELS, COMMAND_STATUS_TONES } from "../lib/device-labels.js";
import {
  BLOCK_TEXTS,
  CHARGE_MODES,
  STATUS_BADGES,
  TECHNICAL_ORDER,
  WAITING_TEXTS,
  changeTitle,
  chargeSentence,
  formatDuration,
  isHardBlock,
  limitInForce,
  maySet,
  modeText,
  pendingCommand,
  percentOf,
  phaseLabel,
  showsLimitSoc,
  switchingSentence,
  type ChargeMode,
  type ChargePoint,
  type ChargePointChange,
} from "../lib/charge-point.js";
import {
  PlanSection,
  RegulationSection,
  SessionSettingsSection,
  VehicleSection,
} from "./charge-point-settings.js";
import { SectionHeading } from "./section.js";

/* -------------------------------------------------------------------------- */

/**
 * Eine Änderung abschicken. Danach lädt **jede** Ansicht eines Ladepunkts nach —
 * und die Auftragsliste des Geräts, die unter „Einstellungen" steht.
 */
export function useChangeChargePoint(deviceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (change: ChargePointChange) => {
      const { data, error } = await api.POST("/charge-points/{deviceId}/changes", {
        params: { path: { deviceId } },
        body: change,
      });
      if (error || !data)
        throw new Error(apiErrorMessage(error) ?? "Der Auftrag ging nicht hinaus.");
      return data;
    },
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["charge-points"] }),
        client.invalidateQueries({ queryKey: ["devices", "commands", deviceId] }),
      ]);
    },
  });
}

/** Ob gerade bedient werden kann — und für wen die Flächen überhaupt erscheinen. */
function controlState(point: ChargePoint, busy: boolean) {
  return {
    /** Ausgeblendet, nicht ausgegraut: Wer nicht steuern darf, sieht keine Knöpfe (Regel 10). */
    shown: point.controllable,
    /** Gesperrt mit Grund: Der Zustand löst sich von selbst. */
    enabled: point.controllable && point.controlBlockedBy === null && !busy,
  };
}

const CAPS = {
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: "var(--tracking-caps)",
  textTransform: "uppercase",
  color: "var(--text-faint)",
} as const;
const MUTED = { fontSize: 12.5, lineHeight: 1.55, color: "var(--text-muted)" } as const;
const MONO = { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" } as const;

/* -------------------------------------------------------------------------- */

/** Der gelbe Kasten, wenn der Ladepunkt gerade nicht bedient werden kann. */
export function ChargeBlockNotice({ point }: { point: ChargePoint }) {
  if (
    !point.controllable ||
    !isHardBlock(point.controlBlockedBy) ||
    point.controlBlockedBy === null
  ) {
    return null;
  }
  const text = BLOCK_TEXTS[point.controlBlockedBy];
  return (
    <Alert tone="warning">
      <strong>{text.title}.</strong> {text.text}
      {point.lastSeenAt !== null && ` Letzte Meldung ${formatTimeAgo(point.lastSeenAt)}.`}
    </Alert>
  );
}

/**
 * Die Zeile unter einer Bedienfläche: was unterwegs ist, was zuletzt galt, was
 * schiefging. Sie ersetzt keinen Verlauf — sie beantwortet die eine Frage nach
 * dem Klick: „Kam das an?"
 */
function CommandLine({
  point,
  error,
  plain,
  trailing,
}: {
  point: ChargePoint;
  error: string | null;
  plain: boolean;
  trailing?: ReactNode;
}) {
  const pending = pendingCommand(point);
  const [latest] = point.commands;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--sp-3)",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "11px 14px",
        borderRadius: "var(--radius-md)",
        background: "var(--surface-inset)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <span style={{ ...MUTED, minWidth: 0, flex: "1 1 260px" }}>
        {error !== null ? (
          <span style={{ color: "var(--tone-danger-fg)" }}>{error}</span>
        ) : pending !== null ? (
          <>
            <strong style={{ color: "var(--text-body)" }}>
              {changeTitle(pending.change, plain, point)}
            </strong>{" "}
            — abgeschickt, wartet auf die Bestätigung des Ladepunkts.
          </>
        ) : latest !== undefined ? (
          <>
            Zuletzt geändert:{" "}
            <strong style={{ color: "var(--text-body)" }}>
              {changeTitle(latest.change, plain, point)}
            </strong>
            , {formatTimeAgo(latest.createdAt)}, {COMMAND_STATUS_LABELS[latest.status]}.
          </>
        ) : (
          "Noch keine Änderung an diesem Ladepunkt."
        )}
      </span>
      {trailing}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Wie geht es gerade?** Oben auf „Laden": Ladestand, ein Satz, die Leistung —
 * und die eine Handlung, die man von dort aus will: pausieren.
 *
 * Der Ring erscheint nur, wenn das Fahrzeug seinen Ladestand meldet. Er trägt
 * die Farbe eines Rohwerts (`--hm-series-datapoint`) und keine Größenfarbe: Ein
 * Ladestand ist keine Energie des Hauses (ADR 0013).
 */
export function ChargeStatusCard({
  point,
  onPause,
  pauseEnabled = true,
}: {
  point: ChargePoint;
  onPause?: () => void;
  pauseEnabled?: boolean;
}) {
  const { state } = point;
  const status = state.status.value === null ? null : STATUS_BADGES[state.status.value];
  const power = state.chargePowerW.value;
  const limit = showsLimitSoc(point) ? limitInForce(point) : null;
  const session = state.sessionEnergyWh.value;
  const remaining = state.session.remainingDurationS.value;
  const solar = state.session.solarShare.value;

  const facts = [
    power !== null && power > 0 ? formatPowerFull(power) : null,
    limit !== null ? `Ziel ${percentOf(limit)} %` : null,
    remaining !== null && remaining > 0 ? `noch ${formatDuration(remaining)}` : null,
    session !== null ? `diese Ladung ${formatEnergyFull(session)}` : null,
    session !== null && session > 0 && solar !== null ? `${percentOf(solar)} % Sonne` : null,
  ].filter((fact): fact is string => fact !== null);
  const switching = switchingSentence(point);

  return (
    <Card>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-5)", alignItems: "center" }}>
        {state.vehicleSoc.value !== null && (
          <Gauge
            value={percentOf(state.vehicleSoc.value)}
            size={112}
            color="var(--hm-series-datapoint)"
            label="Ladestand des Fahrzeugs"
          />
        )}
        <div
          style={{
            flex: "1 1 240px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 7,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            {status !== null && <Badge tone={status.tone}>{status.label}</Badge>}
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {point.name}
              {point.siteAreaName !== null && ` · ${point.siteAreaName}`}
              {state.vehicle.title !== null && ` · ${state.vehicle.title}`}
            </span>
          </span>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.2,
              color: "var(--text-strong)",
            }}
          >
            {chargeSentence(point)}
          </span>
          {facts.length > 0 && (
            <span style={{ ...MONO, fontSize: 13, color: "var(--text-muted)" }}>
              {facts.join(" · ")}
            </span>
          )}
          {switching !== null && (
            <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{switching}</span>
          )}
          {state.measuredAt !== null && (
            <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
              gemeldet {formatTimeAgo(state.measuredAt)}
            </span>
          )}
        </div>
        {onPause !== undefined && (
          <div style={{ flex: "0 0 auto" }}>
            <Button
              variant="secondary"
              leadingIcon={<icons.IconPause size={16} />}
              disabled={!pauseEnabled}
              onClick={onPause}
            >
              Pausieren
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Wie soll geladen werden?** — vier Flächen, die Erklärung einmal darunter:
 * die der gewählten. Im Entwurf trug erst jede Fläche drei Zeilen Text; elf
 * davon braucht niemand.
 */
const MODE_ICONS: Readonly<Record<ChargeMode, typeof icons.IconSun>> = {
  minpv: icons.IconLeaf,
  pv: icons.IconSun,
  now: icons.IconBolt,
  off: icons.IconPause,
};

function ModeTiles({
  point,
  plain,
  enabled,
  onChoose,
}: {
  point: ChargePoint;
  plain: boolean;
  enabled: boolean;
  onChoose: (mode: ChargeMode) => void;
}) {
  const current = point.state.mode.value;
  const pending = pendingCommand(point);
  const pendingMode = pending?.change?.setting === "mode" ? pending.change.value : null;
  const modes = plain ? CHARGE_MODES.map((entry) => entry.mode) : TECHNICAL_ORDER;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: plain
          ? "repeat(auto-fit, minmax(min(100%, 150px), 1fr))"
          : /* Zwei mal zwei, schmal untereinander — drei plus eins wäre unruhiger. */
            "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
        gap: 10,
      }}
    >
      {modes.map((mode) => {
        const text = modeText(mode);
        const active = current === mode;
        const waiting = pendingMode === mode && !active;
        const Glyph = MODE_ICONS[mode];
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={active}
            disabled={!enabled}
            onClick={() => {
              if (!active) onChoose(mode);
            }}
            style={{
              display: "flex",
              flexDirection: plain ? "column" : "row",
              alignItems: plain ? "center" : "flex-start",
              gap: plain ? 8 : 12,
              minHeight: plain ? 92 : undefined,
              padding: plain ? "14px 10px" : "13px 15px",
              textAlign: plain ? "center" : "left",
              cursor: enabled ? "pointer" : "default",
              opacity: enabled || active ? 1 : 0.6,
              fontFamily: "var(--font-body)",
              borderRadius: "var(--radius-lg)",
              background: active ? "var(--hm-green-50)" : "var(--surface-card)",
              border: active
                ? "2px solid var(--hm-green-500)"
                : waiting
                  ? "2px dashed var(--border-strong)"
                  : "1px solid var(--border-default)",
              boxShadow: active ? "none" : "var(--shadow-xs)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                flexShrink: 0,
                width: 34,
                height: 34,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 10,
                background: active ? "var(--hm-green-100)" : "var(--surface-inset)",
                color: active ? "var(--hm-green-700)" : "var(--text-muted)",
              }}
            >
              <Glyph size={19} />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: active ? 700 : 600,
                  lineHeight: 1.3,
                  color: active ? "var(--hm-green-700)" : "var(--text-strong)",
                }}
              >
                {plain ? text.plain : text.label}
              </span>
              {!plain && <span style={MUTED}>{text.text}</span>}
              {waiting && (
                <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
                  wird übernommen …
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Laden** — die einfache Seite für einen Ladepunkt: Zustand, Betriebsart,
 * Ladeziel. Ampere und Phasen stehen am Gerät (`onOpenDevice`), für den, der es
 * eingerichtet hat.
 */
export function ChargingPanel({
  point,
  onOpenDevice,
}: {
  point: ChargePoint;
  onOpenDevice?: () => void;
}) {
  const change = useChangeChargePoint(point.deviceId);
  const [error, setError] = useState<string | null>(null);
  const [help, setHelp] = useState(false);
  const control = controlState(point, change.isPending);

  /**
   * **Ohne je einen Stand gibt es nichts zu bedienen** (HM-183, UI-Durchsicht
   * U8). Bis dahin stand derselbe Satz als Kasten und als Überschrift, darunter
   * Betriebsarten, Plan, Fahrzeug und Ladung — Flächen, die nichts bewirken
   * konnten. Jetzt eine Fläche, die sagt, worauf sie wartet, und den Weg zum Gerät.
   */
  if (point.controlBlockedBy === "never_seen") {
    return (
      <Card>
        <EmptyState
          title="Der Ladepunkt hat sich noch nicht gemeldet"
          icon={<icons.IconPlug size={30} />}
          size="md"
          action={
            onOpenDevice === undefined ? undefined : (
              <Button variant="secondary" size="sm" onClick={onOpenDevice}>
                Gerät öffnen
              </Button>
            )
          }
        >
          Bedienen lässt er sich, sobald sein erster Stand angekommen ist. Er kommt über das Gateway
          herein — meldet sich das Gateway nicht, steht das auf der Seite des Geräts.
        </EmptyState>
      </Card>
    );
  }

  const send = (next: ChargePointChange) => {
    setError(null);
    change.mutate(next, { onError: (cause) => setError(cause.message) });
  };

  const mode = point.state.mode;
  const limit = point.state.limitSoc;
  const current = mode.value === null ? null : modeText(mode.value);
  const choices = [0.6, 0.8, 1];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}>
      <ChargeBlockNotice point={point} />
      <ChargeStatusCard
        point={point}
        onPause={
          control.shown &&
          maySet(point, "mode") &&
          mode.writable &&
          mode.value !== null &&
          mode.value !== "off"
            ? () => send({ setting: "mode", value: "off" })
            : undefined
        }
        pauseEnabled={control.enabled}
      />

      {control.shown && maySet(point, "mode") && mode.writable && (
        <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <SectionHeading title="Wie soll geladen werden?" />
          <ModeTiles
            point={point}
            plain
            enabled={control.enabled}
            onChoose={(value) => send({ setting: "mode", value })}
          />
          {current !== null && (
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              <strong style={{ color: "var(--text-body)" }}>{current.plain}:</strong>{" "}
              {current.plainText}
            </p>
          )}
        </section>
      )}

      {control.shown &&
        maySet(point, "limitSoc") &&
        limit.writable &&
        showsLimitSoc(point) &&
        mode.value !== "off" && (
        <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <SectionHeading title="Ladeziel" />
          <Card>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <span style={CAPS}>Laden bis</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
                {choices.map((ratio) => {
                  const active = limit.value !== null && Math.abs(limit.value - ratio) < 0.005;
                  const pending = pendingCommand(point);
                  const waiting =
                    !active &&
                    pending?.change?.setting === "limitSoc" &&
                    Math.abs(pending.change.value - ratio) < 0.005;
                  return (
                    <button
                      key={ratio}
                      type="button"
                      aria-pressed={active}
                      disabled={!control.enabled}
                      onClick={() => {
                        if (!active) send({ setting: "limitSoc", value: ratio });
                      }}
                      style={{
                        minHeight: 44,
                        padding: "0 17px",
                        cursor: control.enabled ? "pointer" : "default",
                        opacity: control.enabled || active ? 1 : 0.6,
                        fontFamily: "var(--font-body)",
                        fontSize: 14.5,
                        fontWeight: active ? 700 : 500,
                        borderRadius: "var(--radius-pill)",
                        border: active
                          ? "2px solid var(--hm-green-500)"
                          : waiting
                            ? "2px dashed var(--border-strong)"
                            : "1px solid var(--border-default)",
                        background: active ? "var(--hm-green-50)" : "var(--surface-card)",
                        color: active ? "var(--hm-green-700)" : "var(--text-body)",
                      }}
                    >
                      {percentOf(ratio)} %{waiting && " …"}
                    </button>
                  );
                })}
              </div>
            </div>
            <div
              style={{
                marginTop: "var(--sp-4)",
                paddingTop: "var(--sp-3)",
                borderTop: "1px solid var(--border-subtle)",
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--sp-3)",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text-body)" }}>
                {limit.value !== null
                  ? `Lädt bis ${percentOf(limit.value)} %.`
                  : point.state.effectiveLimitSoc.value !== null
                    ? `Kein Ladeziel für diese Ladung — es gilt das des Fahrzeugs: ${percentOf(point.state.effectiveLimitSoc.value)} %.`
                    : "Kein Ladeziel für diese Ladung."}
                {limit.value !== null && (
                  <>
                    {" "}
                    <button
                      type="button"
                      disabled={!control.enabled}
                      onClick={() => send({ setting: "limitSoc", value: 0 })}
                      style={{
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        cursor: control.enabled ? "pointer" : "default",
                        fontFamily: "var(--font-body)",
                        fontSize: 12.5,
                        color: "var(--hm-green-700)",
                      }}
                    >
                      Ziel des Fahrzeugs verwenden
                    </button>
                  </>
                )}
              </span>
              <button
                type="button"
                onClick={() => setHelp((open) => !open)}
                aria-expanded={help}
                style={{
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  fontSize: 12.5,
                  color: "var(--hm-green-700)",
                }}
              >
                {help ? "Erklärung ausblenden" : "Was bedeutet das?"}
              </button>
            </div>
            {help && (
              <p style={{ ...MUTED, margin: "var(--sp-3) 0 0" }}>
                Bei diesem Ladestand des Fahrzeugs endet die Ladung. 80 % schont die Batterie, 100 %
                braucht man vor langen Fahrten. Den Ladestand meldet das Fahrzeug, nicht der
                Ladepunkt — meldet es ihn nicht, lädt der Ladepunkt, solange das Fahrzeug annimmt.
              </p>
            )}
          </Card>
        </section>
      )}

      {control.shown && (
        <>
          <PlanSection point={point} enabled={control.enabled} send={send} />
          <VehicleSection point={point} enabled={control.enabled} send={send} />
          <SessionSettingsSection point={point} enabled={control.enabled} send={send} />
        </>
      )}

      {control.shown && (
        <CommandLine
          point={point}
          error={error}
          plain
          trailing={
            onOpenDevice === undefined ? undefined : (
              <Button variant="ghost" size="sm" onClick={onOpenDevice}>
                Ampere, Phasen und Aufträge
              </Button>
            )
          }
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * **Steuerung** — der Fachreiter am Gerät: Leistung, Betriebsart mit den
 * Begriffen von evcc, Ströme, Phasen, Ladeziel und die Aufträge.
 */
export function ChargePointControl({ point }: { point: ChargePoint }) {
  const change = useChangeChargePoint(point.deviceId);
  const [error, setError] = useState<string | null>(null);
  const control = controlState(point, change.isPending);
  const { state } = point;

  const send = (next: ChargePointChange) => {
    setError(null);
    change.mutate(next, { onError: (cause) => setError(cause.message) });
  };

  const status = state.status.value === null ? null : STATUS_BADGES[state.status.value];
  const facts: readonly { label: string; value: string | null }[] = [
    {
      label: "Ladestand",
      value: state.vehicleSoc.value === null ? null : `${percentOf(state.vehicleSoc.value)} %`,
    },
    {
      label: "Diese Ladung",
      value:
        state.sessionEnergyWh.value === null
          ? null
          : formatEnergyFull(state.sessionEnergyWh.value),
    },
    {
      label: "Geladen gesamt",
      value:
        state.energyImportWh.value === null
          ? null
          : formatEnergyFull(state.energyImportWh.value),
    },
    { label: "Phasen", value: state.phases.value === null ? null : phaseLabel(state.phases.value) },
    {
      label: "Lädt auf",
      value: state.phasesActive.value === null ? null : `${state.phasesActive.value} Phasen`,
    },
    {
      label: "Ladestrom L1 · L2 · L3",
      value: [state.currentL1A, state.currentL2A, state.currentL3A].every(
        (field) => field.value !== null,
      )
        ? `${[state.currentL1A, state.currentL2A, state.currentL3A]
            .map((field) => (field.value ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 1 }))
            .join(" · ")} A`
        : null,
    },
    {
      label: "Spannung L1 · L2 · L3",
      value: [state.voltageL1V, state.voltageL2V, state.voltageL3V].every(
        (field) => field.value !== null,
      )
        ? `${[state.voltageL1V, state.voltageL2V, state.voltageL3V]
            .map((field) => Math.round(field.value ?? 0))
            .join(" · ")} V`
        : null,
    },
    {
      label: "Angebotener Strom",
      value:
        state.offeredCurrentA.value === null
          ? null
          : `${state.offeredCurrentA.value.toLocaleString("de-DE", { maximumFractionDigits: 1 })} A`,
    },
    {
      label: "Ladedauer",
      value:
        state.session.chargeDurationS.value === null || state.session.chargeDurationS.value <= 0
          ? null
          : formatDuration(state.session.chargeDurationS.value),
    },
    {
      label: "Restladezeit",
      value:
        state.session.remainingDurationS.value === null ||
        state.session.remainingDurationS.value <= 0
          ? null
          : formatDuration(state.session.remainingDurationS.value),
    },
    {
      label: "Sonnenanteil",
      value:
        state.session.solarShare.value === null
          ? null
          : `${percentOf(state.session.solarShare.value)} %`,
    },
    {
      label: "Kosten",
      value:
        state.session.priceEur.value === null
          ? null
          : state.session.priceEur.value.toLocaleString("de-DE", {
              style: "currency",
              currency: "EUR",
            }),
    },
    {
      label: "Preis je kWh",
      value:
        state.session.pricePerKwhEur.value === null
          ? null
          : `${(state.session.pricePerKwhEur.value * 100).toLocaleString("de-DE", { maximumFractionDigits: 1 })} ct`,
    },
    {
      label: "CO₂ je kWh",
      value:
        state.session.co2PerKwhG.value === null
          ? null
          : `${Math.round(state.session.co2PerKwhG.value)} g`,
    },
  ];
  const waiting =
    state.waitingFor.value === "authorization" || state.waitingFor.value === "disconnect_required"
      ? WAITING_TEXTS[state.waitingFor.value]
      : null;
  const switching = switchingSentence(point);
  const shownFacts = facts.filter((fact) => fact.value !== null);
  const withLimitSoc =
    showsLimitSoc(point) && state.limitSoc.datapointId !== null && maySet(point, "limitSoc");
  const hasLimits =
    state.minCurrentA.datapointId !== null ||
    state.maxCurrentA.datapointId !== null ||
    state.phases.datapointId !== null ||
    withLimitSoc;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}>
      <ChargeBlockNotice point={point} />

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <SectionHeading
          title="Ladevorgang"
          subtitle={
            state.measuredAt === null
              ? "Der Ladepunkt hat noch keinen Wert gemeldet."
              : `Was der Ladepunkt meldet · gemeldet ${formatTimeAgo(state.measuredAt)}`
          }
        />
        <Card>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--sp-5)",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={CAPS}>Ladeleistung</span>
              <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span
                  style={{
                    ...MONO,
                    fontSize: 34,
                    fontWeight: 700,
                    lineHeight: 1,
                    color: "var(--text-strong)",
                  }}
                >
                  {state.chargePowerW.value === null ? "–" : powerParts(state.chargePowerW.value).text}
                </span>
                <span style={{ fontSize: 14, color: "var(--text-muted)" }}>
                  {state.chargePowerW.value === null ? POWER_UNIT : powerParts(state.chargePowerW.value).unit}
                </span>
              </span>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}
            >
              {status !== null && <Badge tone={status.tone}>{status.label}</Badge>}
              {state.mode.value !== null && (
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                  Betriebsart {modeText(state.mode.value).label}
                </span>
              )}
              {state.enabled.value === false && state.status.value !== "disconnected" && (
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                  Ladung nicht freigegeben
                </span>
              )}
            </div>
          </div>
          {(waiting !== null || switching !== null) && (
            <p style={{ ...MUTED, margin: "var(--sp-3) 0 0" }}>
              {[waiting, switching].filter((line) => line !== null).join(" · ")}
            </p>
          )}
          {shownFacts.length > 0 && (
            <div
              style={{
                marginTop: "var(--sp-4)",
                paddingTop: "var(--sp-4)",
                borderTop: "1px solid var(--border-subtle)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))",
                gap: "var(--sp-4)",
              }}
            >
              {shownFacts.map((fact) => (
                <div key={fact.label}>
                  <div style={CAPS}>{fact.label}</div>
                  <div
                    style={{
                      ...MONO,
                      marginTop: 3,
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--text-strong)",
                    }}
                  >
                    {fact.value}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>

      {control.shown && maySet(point, "mode") && state.mode.writable && (
        <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <SectionHeading
            title="Betriebsart"
            subtitle="Gilt, sobald der Ladepunkt sie bestätigt. Bis dahin steht hier die bisherige."
          />
          <ModeTiles
            point={point}
            plain={false}
            enabled={control.enabled}
            onChoose={(value) => send({ setting: "mode", value })}
          />
        </section>
      )}

      {control.shown && hasLimits && (
        <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <SectionHeading
            title={withLimitSoc ? "Grenzen und Ladeziel" : "Grenzen"}
            subtitle="Jede Grenze ist ein eigener Auftrag — der Ladepunkt nimmt einen zur Zeit an."
          />
          <Card>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 230px), 1fr))",
                gap: "var(--sp-5)",
                alignItems: "start",
              }}
            >
              <Stepper
                label="Mindeststrom"
                unit="A"
                field={state.minCurrentA}
                min={6}
                max={state.maxCurrentA.value ?? 32}
                enabled={control.enabled}
                hint="Dieser Strom wird bei „Min + PV“ auch ohne Überschuss freigegeben. Unter 6 A lädt kein Fahrzeug."
                onSend={(value) => send({ setting: "minCurrent", value })}
              />
              <Stepper
                label="Maximalstrom"
                unit="A"
                field={state.maxCurrentA}
                min={state.minCurrentA.value ?? 6}
                max={32}
                enabled={control.enabled}
                hint="Grenze von Ladepunkt und Zuleitung. Höher als die Absicherung geht nicht."
                onSend={(value) => send({ setting: "maxCurrent", value })}
              />
              {withLimitSoc && (
                <Stepper
                  label="Ladeziel"
                  unit="%"
                  field={state.limitSoc}
                  toShown={percentOf}
                  fromShown={(percent) => percent / 100}
                  min={20}
                  max={100}
                  step={5}
                  enabled={control.enabled}
                  hint="Bei diesem Ladestand des Fahrzeugs endet die Ladung."
                  onSend={(value) => send({ setting: "limitSoc", value })}
                />
              )}
              {state.phases.writable && maySet(point, "phases") && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)" }}>
                    Phasen
                  </span>
                  {state.phases.value === null ? (
                    <span style={MUTED}>noch kein Wert gemeldet</span>
                  ) : (
                    <div
                      style={{
                        opacity: control.enabled ? 1 : 0.6,
                        pointerEvents: control.enabled ? "auto" : "none",
                      }}
                    >
                      <SegmentedControl
                        size="sm"
                        label="Phasen"
                        value={String(state.phases.value)}
                        options={state.phaseChoices.map((choice) => ({
                          value: String(choice),
                          label: phaseLabel(choice),
                        }))}
                        onChange={(next) => {
                          const value = Number(next);
                          if (value !== state.phases.value) send({ setting: "phases", value });
                        }}
                      />
                    </div>
                  )}
                  <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--text-muted)" }}>
                    Einphasig nutzt kleinen Überschuss; automatisch schaltet evcc um, wenn der
                    Überschuss es zulässt.
                  </span>
                </div>
              )}
            </div>
          </Card>
        </section>
      )}

      {control.shown && (
        <>
          <SessionSettingsSection point={point} enabled={control.enabled} send={send} />
          <VehicleSection point={point} enabled={control.enabled} send={send} />
          <PlanSection point={point} enabled={control.enabled} send={send} />
          <RegulationSection point={point} enabled={control.enabled} send={send} />
        </>
      )}

      {/* Nur, wenn es etwas zu sagen gibt: Den Verlauf zeigt die Liste darunter. */}
      {control.shown && (error !== null || pendingCommand(point) !== null) && (
        <CommandLine point={point} error={error} plain={false} />
      )}

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <SectionHeading
          title="Aufträge"
          subtitle="Was an diesen Ladepunkt geschickt wurde. „Keine Antwort“ heißt: abgeschickt, nicht bestätigt — der alte Wert gilt weiter."
        />
        <Card padding="none">
          {point.commands.length === 0 ? (
            <p style={{ ...MUTED, margin: 0, padding: "14px 16px" }}>
              Noch keine Änderung an diesem Ladepunkt.
            </p>
          ) : (
            point.commands.map((command, index) => (
              <div
                key={command.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: "var(--sp-3)",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderBottom:
                    index < point.commands.length - 1 ? "1px solid var(--border-subtle)" : "none",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)" }}>
                    {changeTitle(command.change, false, point)}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-faint)" }}>
                    {formatTimeAgo(command.createdAt)}
                    {command.message !== null && ` · ${command.message}`}
                  </div>
                </div>
                <Badge tone={COMMAND_STATUS_TONES[command.status]}>
                  {COMMAND_STATUS_LABELS[command.status]}
                </Badge>
              </div>
            ))
          )}
        </Card>
      </section>

      <p style={{ ...MUTED, margin: 0 }}>
        <strong>Gesteuert wird über evcc, gemessen am Zähler.</strong> Die Ladeenergie hier meldet
        der Ladepunkt; abgerechnet wird sein Zähler — beide Zahlen können um die Ladeverluste
        auseinanderliegen.
        {point.siteAreaName !== null &&
          ` Der Ladepunkt gehört über seinen Zähler zu „${point.siteAreaName}“; wer dort wohnt, bedient ihn unter „Laden“ ebenfalls.`}
      </p>
    </div>
  );
}

/**
 * Ein Zahlenfeld mit Schrittknöpfen und eigenem „Setzen". **Strom in Ampere,
 * nicht in Kilowatt**: Der Ladepunkt regelt Strom, und was daraus an Leistung
 * wird, hängt an den Phasen.
 */
export function Stepper({
  label,
  unit,
  field,
  min,
  max,
  step = 1,
  toShown = (value) => value,
  fromShown = (value) => value,
  enabled,
  hint,
  onSend,
}: {
  label: string;
  unit: string;
  field: ChargePoint["state"]["minCurrentA"];
  min: number;
  max: number;
  step?: number;
  toShown?: (value: number) => number;
  fromShown?: (value: number) => number;
  enabled: boolean;
  hint: string;
  onSend: (value: number) => void;
}) {
  const reported = field.value === null ? null : toShown(field.value);
  const [draft, setDraft] = useState<number | null>(null);
  if (!field.writable) return null;

  const shown = draft ?? reported;
  const changed = draft !== null && draft !== reported;
  const nudge = (delta: number) => setDraft(Math.min(max, Math.max(min, (shown ?? min) + delta)));
  const stepButton = {
    width: 34,
    height: 34,
    flexShrink: 0,
    border: "1px solid var(--border-default)",
    borderRadius: "var(--radius-md)",
    background: "var(--surface-card)",
    color: "var(--text-strong)",
    fontSize: 16,
    cursor: enabled ? "pointer" : "default",
  } as const;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)" }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={!enabled}
          onClick={() => nudge(-step)}
          aria-label={`${label} verringern`}
          style={stepButton}
        >
          −
        </button>
        <span
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "center",
            gap: 4,
            minWidth: 84,
            height: 34,
            padding: "0 10px",
            lineHeight: "34px",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-card)",
          }}
        >
          <span
            style={{
              ...MONO,
              fontSize: 15,
              fontWeight: 700,
              color: shown === null ? "var(--text-faint)" : "var(--text-strong)",
            }}
          >
            {shown ?? "–"}
          </span>
          {shown !== null && (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{unit}</span>
          )}
        </span>
        <button
          type="button"
          disabled={!enabled}
          onClick={() => nudge(step)}
          aria-label={`${label} erhöhen`}
          style={stepButton}
        >
          +
        </button>
        {changed && (
          <Button
            variant="primary"
            size="sm"
            disabled={!enabled}
            onClick={() => {
              if (shown !== null) onSend(fromShown(shown));
              setDraft(null);
            }}
          >
            Setzen
          </Button>
        )}
      </div>
      <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--text-muted)" }}>{hint}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

